import React, { createContext, useState, useContext, useCallback } from 'react'
import api from '../services/api'
import dashboardService from '../services/dashboardService'
import { useSnackbar } from 'notistack'

const MRDContext = createContext({})

export const useMRD = () => useContext(MRDContext)

export const MRDProvider = ({ children }) => {
  const [files, setFiles] = useState([])
  const [selectedFile, setSelectedFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState({})
  const [masterDataStats, setMasterDataStats] = useState({})
  const { enqueueSnackbar } = useSnackbar()

  const fetchFiles = useCallback(async (params = {}) => {
    setLoading(true)
    try {
      const response = await api.get('/mrd/files', { params })
      console.log('Files API response:', response.data)
      const filesData = response.data.data || []
      console.log('Files loaded:', filesData.length, 'files')
      console.log('Pagination info:', response.data.pagination)
      console.log('First file sample:', filesData[0])
      setFiles(Array.isArray(filesData) ? filesData : [])
      return response.data
    } catch (error) {
      console.error('Fetch files error:', error)
      enqueueSnackbar('Failed to fetch files', { variant: 'error' })
      throw error
    } finally {
      setLoading(false)
    }
  }, [enqueueSnackbar])

  const fetchFileById = useCallback(async (id) => {
    setLoading(true)
    try {
      const response = await api.get(`/mrd/files/${id}`)
      setSelectedFile(response.data.data)
      return response.data
    } catch (error) {
      enqueueSnackbar('Failed to fetch file details', { variant: 'error' })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const createFile = async (fileData) => {
    try {
      const response = await api.post('/mrd/files', fileData)
      enqueueSnackbar('MRD File created successfully', { variant: 'success' })
      return response.data
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to create file',
        { variant: 'error' }
      )
      throw error
    }
  }

  const updateFile = async (fileId, fileData) => {
    try {
      const response = await api.put(`/mrd/files/${fileId}`, fileData)
      enqueueSnackbar('MRD File updated successfully', { variant: 'success' })
      return response.data
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to update file',
        { variant: 'error' }
      )
      throw error
    }
  }

  const updateFileStatus = async (fileId, status, remarks = '') => {
    try {
      const response = await api.patch(`/mrd/files/${fileId}/status`, {
        status,
        remarks
      })
      enqueueSnackbar(`Status updated to ${status}`, { variant: 'success' })
      
      // Update local state
      setFiles(prev => prev.map(file => 
        file._id === fileId ? { ...file, fileStatus: status } : file
      ))
      if (selectedFile?._id === fileId) {
        setSelectedFile(prev => ({ ...prev, fileStatus: status }))
      }
      
      return response.data
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to update status',
        { variant: 'error' }
      )
      throw error
    }
  }

  const fetchDashboardStats = async () => {
    try {
      const response = await api.get('/mrd/dashboard/stats')
      setStats(response.data.data)
      return response.data
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error)
      throw error
    }
  }

  const fetchMasterDataStats = async () => {
    try {
      const response = await dashboardService.getMasterDataStats()
      setMasterDataStats(response.data.data)
      return response.data
    } catch (error) {
      console.error('Failed to fetch master data stats:', error)
      throw error
    }
  }

  const value = {
    files,
    selectedFile,
    loading,
    stats,
    masterDataStats,
    fetchFiles,
    fetchFileById,
    createFile,
    updateFile,
    updateFileStatus,
    fetchDashboardStats,
    fetchMasterDataStats,
    setSelectedFile
  }

  return (
    <MRDContext.Provider value={value}>
      {children}
    </MRDContext.Provider>
  )
}